import { useState, useEffect, useCallback } from 'react';
import { type VendorWithRelations, type User, isAdmin, isPlannerOrAbove } from '@peles/shared';
import {
  useVendor,
  useVendors,
  useUpdateVendor,
  useDeactivateVendor,
  useDeleteVendor,
  useVendorUsage,
  useAddVendorCategory,
  useRemoveVendorCategory,
  useVendorCategories,
} from '../../hooks/useVendors';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { ReassignDeleteModal } from '../common/ReassignDeleteModal';
import { GoogleAddressAutocomplete } from '../common/GoogleAddressAutocomplete';
import { useDialog } from '../../context/DialogContext';

interface VendorDetailPanelProps {
  vendorId: string;
  onClose: () => void;
  onUnsavedChange: (dirty: boolean) => void;
  currentUser: User;
}

interface FormState {
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  taxId: string;
  website: string;
  rating: string;
  notes: string;
  isActive: boolean;
}

function vendorToFormState(vendor: VendorWithRelations): FormState {
  return {
    businessName: vendor.businessName,
    contactName: vendor.contactName || '',
    email: vendor.email || '',
    phone: vendor.phone || '',
    address: vendor.address || '',
    city: vendor.city || '',
    state: vendor.state || '',
    zipCode: vendor.zipCode || '',
    country: vendor.country || '',
    taxId: vendor.taxId || '',
    website: vendor.website || '',
    rating: vendor.rating?.toString() || '',
    notes: vendor.notes || '',
    isActive: vendor.isActive,
  };
}

function isDirty(form: FormState, original: FormState): boolean {
  return (
    form.businessName !== original.businessName ||
    form.contactName !== original.contactName ||
    form.email !== original.email ||
    form.phone !== original.phone ||
    form.address !== original.address ||
    form.city !== original.city ||
    form.state !== original.state ||
    form.zipCode !== original.zipCode ||
    form.country !== original.country ||
    form.taxId !== original.taxId ||
    form.website !== original.website ||
    form.rating !== original.rating ||
    form.notes !== original.notes ||
    form.isActive !== original.isActive
  );
}

export function VendorDetailPanel({
  vendorId,
  onClose,
  onUnsavedChange,
  currentUser,
}: VendorDetailPanelProps) {
  const { confirm } = useDialog();
  const { data: vendor, isLoading } = useVendor(vendorId);
  const updateVendor = useUpdateVendor();
  const deactivateVendor = useDeactivateVendor();
  const deleteVendor = useDeleteVendor();
  const addCategory = useAddVendorCategory(vendorId);
  const removeCategory = useRemoveVendorCategory(vendorId);
  const { data: allCategories = [] } = useVendorCategories();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { data: usage } = useVendorUsage(vendorId, showDeleteModal);
  // Replacement candidates are only needed once the delete modal is open.
  const { data: allVendors = [] } = useVendors(undefined, { enabled: showDeleteModal });

  const [form, setForm] = useState<FormState>({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    taxId: '',
    website: '',
    rating: '',
    notes: '',
    isActive: true,
  });
  const [originalForm, setOriginalForm] = useState<FormState>(form);
  const [saved, setSaved] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  const currentUserIsAdmin = isAdmin(currentUser.systemRole);
  const currentUserIsPlanner = isPlannerOrAbove(currentUser.systemRole);

  // Initialize form when vendor data loads
  useEffect(() => {
    if (vendor) {
      const state = vendorToFormState(vendor);
      setForm(state);
      setOriginalForm(state);
      setSaved(false);
    }
  }, [vendor]);

  // Track dirty state
  const dirty = isDirty(form, originalForm);
  useEffect(() => {
    onUnsavedChange(dirty);
  }, [dirty, onUnsavedChange]);

  const handleSave = useCallback(() => {
    const updates: Record<string, unknown> = {};
    if (form.businessName !== originalForm.businessName) updates.businessName = form.businessName;
    if (form.contactName !== originalForm.contactName)
      updates.contactName = form.contactName || null;
    if (form.email !== originalForm.email) updates.email = form.email || null;
    if (form.phone !== originalForm.phone) updates.phone = form.phone || null;
    if (form.address !== originalForm.address) updates.address = form.address || null;
    if (form.city !== originalForm.city) updates.city = form.city || null;
    if (form.state !== originalForm.state) updates.state = form.state || null;
    if (form.zipCode !== originalForm.zipCode) updates.zipCode = form.zipCode || null;
    if (form.country !== originalForm.country) updates.country = form.country || null;
    if (form.taxId !== originalForm.taxId) updates.taxId = form.taxId || null;
    if (form.website !== originalForm.website) updates.website = form.website || null;
    if (form.rating !== originalForm.rating) {
      updates.rating = form.rating ? parseFloat(form.rating) : null;
    }
    if (form.notes !== originalForm.notes) updates.notes = form.notes || null;
    if (form.isActive !== originalForm.isActive) updates.isActive = form.isActive;

    updateVendor.mutate(
      { id: vendorId, updates },
      {
        onSuccess: (updated) => {
          const state = vendorToFormState(updated);
          setForm(state);
          setOriginalForm(state);
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        },
      },
    );
  }, [form, originalForm, vendorId, updateVendor]);

  const handleDeactivate = useCallback(async () => {
    if (
      await confirm({
        title: 'Deactivate Vendor',
        message: `Deactivate vendor "${vendor?.businessName}"? This action cannot be undone.`,
        confirmLabel: 'Deactivate',
      })
    ) {
      deactivateVendor.mutate(vendorId);
    }
  }, [vendorId, vendor?.businessName, deactivateVendor, confirm]);

  const handleAddCategory = useCallback(
    (categoryId: string | string[]) => {
      const id = Array.isArray(categoryId) ? categoryId[0] : categoryId;
      if (!id) return;
      addCategory.mutate(
        { vendorCategoryId: id },
        {
          onSuccess: () => {
            setSelectedCategoryId(null);
          },
        },
      );
    },
    [addCategory],
  );

  const handleRemoveCategory = useCallback(
    (vendorCategoryId: string) => {
      removeCategory.mutate(vendorCategoryId);
    },
    [removeCategory],
  );

  // Categories already assigned to this vendor
  const assignedCategoryIds = vendor?.categories.map((c) => c.vendorCategoryId) ?? [];

  // Available categories (exclude already assigned ones)
  const availableCategories = allCategories.filter(
    (c) => c.isActive && !assignedCategoryIds.includes(c.id),
  );

  if (isLoading) {
    return (
      <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white">
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white">
        <p className="text-gray-500 text-sm">Vendor not found</p>
      </div>
    );
  }

  return (
    <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white overflow-auto">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-lg font-semibold flex-1">Vendor Details</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label="Close panel"
        >
          &times;
        </button>
      </div>

      {saved && (
        <div className="mb-4 px-3 py-2 bg-green-50 text-green-700 text-sm rounded">
          Changes saved successfully
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label
            htmlFor="detail-vendor-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Business Name
          </label>
          <input
            id="detail-vendor-name"
            type="text"
            value={form.businessName}
            onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))}
            disabled={!currentUserIsPlanner}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
          />
        </div>

        {/* Categories section — saves immediately, separate from main Save */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Categories</label>
          {vendor.categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {vendor.categories.map((cat) => (
                <span
                  key={cat.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-800 text-xs rounded-full"
                >
                  {cat.categoryName}
                  {cat.isPrimary && (
                    <span className="ml-1 px-1 py-0 bg-primary-600 text-white text-xs rounded">
                      Primary
                    </span>
                  )}
                  {currentUserIsPlanner && (
                    <button
                      type="button"
                      onClick={() => handleRemoveCategory(cat.vendorCategoryId)}
                      disabled={removeCategory.isPending}
                      className="ml-0.5 text-primary-600 hover:text-primary-900 disabled:opacity-50"
                      aria-label={`Remove ${cat.categoryName}`}
                    >
                      &times;
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          {vendor.categories.length === 0 && (
            <p className="text-xs text-gray-400 mb-2">No categories assigned</p>
          )}
          {currentUserIsPlanner && availableCategories.length > 0 && (
            <SearchableListSelect
              items={availableCategories}
              value={selectedCategoryId}
              onChange={handleAddCategory}
              getValue={(c) => c.id}
              getLabel={(c) => c.name}
              placeholder="Add category..."
              disabled={addCategory.isPending}
              clearable={false}
            />
          )}
        </div>

        <div>
          <label
            htmlFor="detail-vendor-contact"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Contact Name
          </label>
          <input
            id="detail-vendor-contact"
            type="text"
            value={form.contactName}
            onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))}
            disabled={!currentUserIsPlanner}
            placeholder="Optional"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="detail-vendor-email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              id="detail-vendor-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              disabled={!currentUserIsPlanner}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
            />
          </div>
          <div>
            <label
              htmlFor="detail-vendor-phone"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Phone
            </label>
            <input
              id="detail-vendor-phone"
              type="text"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              disabled={!currentUserIsPlanner}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="detail-vendor-address"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Address
          </label>
          <GoogleAddressAutocomplete
            id="detail-vendor-address"
            value={form.address}
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            onAddressSelect={(fields) =>
              setForm((f) => ({
                ...f,
                city: fields.city || f.city,
                state: fields.state || f.state,
                zipCode: fields.zipCode || f.zipCode,
                country: fields.country || f.country,
              }))
            }
            disabled={!currentUserIsPlanner}
            placeholder="Optional"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="detail-vendor-city"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              City
            </label>
            <input
              id="detail-vendor-city"
              type="text"
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              disabled={!currentUserIsPlanner}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
            />
          </div>
          <div>
            <label
              htmlFor="detail-vendor-state"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              State
            </label>
            <input
              id="detail-vendor-state"
              type="text"
              value={form.state}
              onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
              disabled={!currentUserIsPlanner}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="detail-vendor-zip"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              ZIP Code
            </label>
            <input
              id="detail-vendor-zip"
              type="text"
              value={form.zipCode}
              onChange={(e) => setForm((f) => ({ ...f, zipCode: e.target.value }))}
              disabled={!currentUserIsPlanner}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
            />
          </div>
          <div>
            <label
              htmlFor="detail-vendor-taxid"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Tax ID
            </label>
            <input
              id="detail-vendor-taxid"
              type="text"
              value={form.taxId}
              onChange={(e) => setForm((f) => ({ ...f, taxId: e.target.value }))}
              disabled={!currentUserIsPlanner}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="detail-vendor-country"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Country
          </label>
          <input
            id="detail-vendor-country"
            type="text"
            value={form.country}
            onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
            disabled={!currentUserIsPlanner}
            placeholder="Optional"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
          />
        </div>

        <div>
          <label
            htmlFor="detail-vendor-website"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Website
          </label>
          <input
            id="detail-vendor-website"
            type="url"
            value={form.website}
            onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
            disabled={!currentUserIsPlanner}
            placeholder="https://example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
          />
        </div>

        <div>
          <label
            htmlFor="detail-vendor-rating"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Rating (0-5)
          </label>
          <input
            id="detail-vendor-rating"
            type="number"
            value={form.rating}
            onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))}
            disabled={!currentUserIsPlanner}
            placeholder="Optional"
            min="0"
            max="5"
            step="0.1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
          />
        </div>

        <div>
          <label
            htmlFor="detail-vendor-notes"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Notes
          </label>
          <textarea
            id="detail-vendor-notes"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            disabled={!currentUserIsPlanner}
            placeholder="Optional"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none disabled:bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
          <p className="text-sm text-gray-600 px-3 py-2 bg-gray-50 rounded-md">
            {new Date(vendor.createdAt).toLocaleDateString()}
          </p>
        </div>
      </div>

      {currentUserIsPlanner && (
        <div className="mt-6 flex gap-3">
          <button
            onClick={handleSave}
            disabled={!dirty || updateVendor.isPending}
            className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateVendor.isPending ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Danger Zone */}
      {currentUserIsAdmin && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-red-600 mb-3">Danger Zone</h3>
          <div className="flex gap-3">
            {vendor.isActive && (
              <button
                onClick={handleDeactivate}
                disabled={deactivateVendor.isPending}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deactivateVendor.isPending ? 'Deactivating...' : 'Deactivate Vendor'}
              </button>
            )}
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-4 py-2 border border-red-600 text-red-600 text-sm rounded-md hover:bg-red-50"
            >
              Delete Vendor
            </button>
          </div>
        </div>
      )}

      <ReassignDeleteModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        entityTypeLabel="vendor"
        entityName={vendor.businessName}
        usage={
          usage && [
            { label: 'Expenses', count: usage.expenseItems },
            { label: 'Quotes', count: usage.quotes },
            { label: 'Invoices', count: usage.invoices },
            { label: 'Timeline entries', count: usage.timelineEntries },
            { label: 'Venues', count: usage.venues },
            { label: 'Commissions', count: usage.commissions },
            { label: 'Questionnaire answers', count: usage.questionnaireAnswers },
            { label: 'Event & entity links', count: usage.entityLinks },
          ]
        }
        options={allVendors.map((v) => ({
          id: v.id,
          label: v.businessName,
          subtitle: [v.city, v.phone].filter(Boolean).join(' · ') || undefined,
        }))}
        excludeId={vendorId}
        onConfirm={async (reassignToId) => {
          await deleteVendor.mutateAsync({ id: vendorId, reassignToVendorId: reassignToId });
          onClose();
        }}
        deleting={deleteVendor.isPending}
      />
    </div>
  );
}
